import { useTheme } from 'styled-components';
import { getToken, Size } from '@optimus-web/common';
import { OptimusTextKey, useLocalization } from '@optimus-web/localization';
import { getEndStrokeDashoffsetValue, getSizeProperties } from './helpers';

interface SpinnerProps {
    size?: Exclude<Size, 'extraLarge'>;
}

export const Spinner: React.FC<SpinnerProps> = ({ size = 'medium', ...other }) => {
    const theme = useTheme();
    const { t } = useLocalization();
    const { dimension, radius, strokeWidth } = getSizeProperties(size, theme);
    const value = getEndStrokeDashoffsetValue(size);

    const circleCommonProps = {
        cx: dimension / 2,
        cy: dimension / 2,
        r: radius,
        strokeWidth,
        fill: 'none',
        strokeLinecap: 'round' as const,
    };

    const animateCommonProps = {
        dur: '1.5s',
        repeatCount: 'indefinite',
        keyTimes: '0;1',
        keySplines: '0.48 0 0.4 1',
        calcMode: 'spline',
    };

    return (
        <svg
            width={dimension}
            height={dimension}
            viewBox={`0 0 ${dimension} ${dimension}`}
            transform="rotate(-90)"
            data-test-spinner
            role="status"
            {...other}
        >
            <title>{t(OptimusTextKey.Loading)}</title>
            <circle
                {...circleCommonProps}
                stroke={getToken('backgroundInteractiveNeutralBoldDefault')({ theme })}
                opacity={getToken('opacity200')({ theme })}
            >
                <animate
                    {...animateCommonProps}
                    attributeName="stroke-dasharray"
                    values="1,360; 90,360"
                    begin="-0.1s"
                />
                <animate
                    {...animateCommonProps}
                    attributeName="stroke-dashoffset"
                    values={`0; ${value}`}
                    begin="-0.1s"
                />
            </circle>
            <circle {...circleCommonProps} stroke={getToken('backgroundAccentSecondary')({ theme })}>
                <animate
                    {...animateCommonProps}
                    attributeName="stroke-dasharray"
                    values="1,360; 90,360"
                    begin="-0.2s"
                />
                <animate
                    {...animateCommonProps}
                    attributeName="stroke-dashoffset"
                    values={`0; ${value}`}
                    begin="-0.2s"
                />
            </circle>
            <circle {...circleCommonProps} stroke={getToken('backgroundAccentPrimary')({ theme })}>
                <animate
                    {...animateCommonProps}
                    attributeName="stroke-dasharray"
                    values="1,360; 90,360"
                    begin="-0.3s"
                />
                <animate
                    {...animateCommonProps}
                    attributeName="stroke-dashoffset"
                    values={`0; ${value}`}
                    begin="-0.3s"
                />
            </circle>
        </svg>
    );
};
